import type { CheckoutPaymentMethod, DeliveryMethod } from "../../api/types.js";
import { messages } from "../../i18n/messages.js";

export interface CheckoutFormValues {
  full_name: string;
  phone: string;
  delivery_method: DeliveryMethod;
  city: string;
  warehouse: string;
  address: string;
  comment: string;
  payment_method: CheckoutPaymentMethod;
}

interface CheckoutFormProps {
  values: CheckoutFormValues;
  onChange: (values: CheckoutFormValues) => void;
  disabled?: boolean;
  fieldErrors?: Record<string, string>;
}

const DELIVERY_OPTIONS: Array<{ value: DeliveryMethod; label: string }> = [
  { value: "nova_poshta_warehouse", label: messages.checkout.deliveryOptions.nova_poshta_warehouse },
  { value: "nova_poshta_courier", label: messages.checkout.deliveryOptions.nova_poshta_courier },
  { value: "pickup", label: messages.checkout.deliveryOptions.pickup },
  { value: "other", label: messages.checkout.deliveryOptions.other },
];

const PAYMENT_OPTIONS: Array<{ value: CheckoutPaymentMethod; label: string }> = [
  { value: "cod", label: messages.checkout.paymentOptions.cod },
  { value: "prepaid", label: messages.checkout.paymentOptions.prepaid },
  { value: "novapay", label: messages.checkout.paymentOptions.novapay },
  { value: "manager", label: messages.checkout.paymentOptions.manager },
];

function fieldClassName(hasError?: boolean): string {
  return `min-h-11 w-full rounded-[14px] border ${
    hasError ? "border-red-400" : "border-black/10"
  } bg-[var(--tg-theme-bg-color,#fff)] px-3 py-2.5 text-sm`;
}

export function CheckoutForm({
  values,
  onChange,
  disabled = false,
  fieldErrors = {},
}: CheckoutFormProps) {
  function patch(partial: Partial<CheckoutFormValues>) {
    onChange({ ...values, ...partial });
  }

  const showCity =
    values.delivery_method === "nova_poshta_warehouse" ||
    values.delivery_method === "nova_poshta_courier";
  const showWarehouse = values.delivery_method === "nova_poshta_warehouse";
  const showAddress =
    values.delivery_method === "nova_poshta_courier" || values.delivery_method === "other";

  return (
    <div className="flex flex-col gap-4">
      <fieldset className="flex flex-col gap-3 rounded-[16px] border border-black/5 p-4" disabled={disabled}>
        <legend className="px-1 text-sm font-semibold">{messages.checkout.contact}</legend>
        <label className="flex flex-col gap-1 text-sm">
          <span>{messages.checkout.fullName}</span>
          <input
            type="text"
            className={fieldClassName(Boolean(fieldErrors.full_name))}
            value={values.full_name}
            onChange={(e) => patch({ full_name: e.target.value })}
            autoComplete="name"
          />
          {fieldErrors.full_name ? (
            <span className="text-xs text-red-600">{fieldErrors.full_name}</span>
          ) : null}
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span>{messages.checkout.phone}</span>
          <input
            type="tel"
            className={fieldClassName(Boolean(fieldErrors.phone))}
            value={values.phone}
            onChange={(e) => patch({ phone: e.target.value })}
            autoComplete="tel"
            placeholder="+380..."
          />
          {fieldErrors.phone ? <span className="text-xs text-red-600">{fieldErrors.phone}</span> : null}
        </label>
      </fieldset>

      <fieldset className="flex flex-col gap-3 rounded-[16px] border border-black/5 p-4" disabled={disabled}>
        <legend className="px-1 text-sm font-semibold">{messages.checkout.delivery}</legend>
        <label className="flex flex-col gap-1 text-sm">
          <span>{messages.checkout.method}</span>
          <select
            className={fieldClassName()}
            value={values.delivery_method}
            onChange={(e) => patch({ delivery_method: e.target.value as DeliveryMethod })}
          >
            {DELIVERY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        {showCity ? (
          <label className="flex flex-col gap-1 text-sm">
            <span>{messages.checkout.city}</span>
            <input
              type="text"
              className={fieldClassName(Boolean(fieldErrors.city))}
              value={values.city}
              onChange={(e) => patch({ city: e.target.value })}
            />
          </label>
        ) : null}
        {showWarehouse ? (
          <label className="flex flex-col gap-1 text-sm">
            <span>{messages.checkout.warehouse}</span>
            <input
              type="text"
              className={fieldClassName(Boolean(fieldErrors.warehouse))}
              value={values.warehouse}
              onChange={(e) => patch({ warehouse: e.target.value })}
            />
          </label>
        ) : null}
        {showAddress ? (
          <label className="flex flex-col gap-1 text-sm">
            <span>{messages.checkout.address}</span>
            <textarea
              className={fieldClassName(Boolean(fieldErrors.address))}
              rows={3}
              value={values.address}
              onChange={(e) => patch({ address: e.target.value })}
            />
          </label>
        ) : null}
        <label className="flex flex-col gap-1 text-sm">
          <span>{messages.checkout.comment}</span>
          <textarea
            className={fieldClassName()}
            rows={2}
            value={values.comment}
            onChange={(e) => patch({ comment: e.target.value })}
          />
        </label>
      </fieldset>

      <fieldset className="flex flex-col gap-3 rounded-[16px] border border-black/5 p-4" disabled={disabled}>
        <legend className="px-1 text-sm font-semibold">{messages.checkout.payment}</legend>
        <label className="flex flex-col gap-1 text-sm">
          <span>{messages.checkout.method}</span>
          <select
            className={fieldClassName()}
            value={values.payment_method}
            onChange={(e) => patch({ payment_method: e.target.value as CheckoutPaymentMethod })}
          >
            {PAYMENT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <p className="text-xs opacity-60">{messages.checkout.paymentNote}</p>
        <p className="text-xs opacity-60">{messages.checkout.npNote}</p>
      </fieldset>
    </div>
  );
}
